import { useCallback, useEffect, useRef, useState } from 'react';
import { transcribeLocally } from '../lib/localWhisper';

/**
 * Tracks the loading state of the fully local, in-browser Whisper model and
 * exposes a `transcribeChunk` function compatible with useAudioCapture.
 */
export function useLocalWhisperModel(quality = 'balanced') {
  const [modelStatus, setModelStatus] = useState('idle'); // idle | loading | ready | error
  const [progress, setProgress] = useState(0);
  const loadingStarted = useRef(false);
  useEffect(() => {
    loadingStarted.current = false;
    setModelStatus('idle');
    setProgress(0);
  }, [quality]);

  const handleProgress = useCallback((event) => {
    if (event.status === 'progress' && Number.isFinite(event.progress)) {
      setProgress(Math.round(event.progress));
    }
  }, []);

  const transcribeChunk = useCallback(
    async (blob) => {
      if (!loadingStarted.current) {
        loadingStarted.current = true;
        setModelStatus('loading');
      }
      try {
        const text = await transcribeLocally(blob, handleProgress, quality);
        setModelStatus('ready');
        return text;
      } catch (err) {
        console.error('Local transcription failed', err);
        loadingStarted.current = false;
        setModelStatus('error');
        throw err;
      }
    },
    [handleProgress, quality]
  );

  return { transcribeChunk, modelStatus, progress };
}
